/**
 * Orientation du score d'un match — source unique pour tout le frontend.
 *
 * LA CONVENTION
 * -------------
 * `score_home` est TOUJOURS le score de l'equipe qui RECOIT, jamais celui du
 * club. `is_home` dit de quel cote le club se trouve. Sur un match joue a
 * l'exterieur, `score_home` est donc le score de l'ADVERSAIRE.
 *
 * Elle n'est pas supposee : elle est imposee par le code de saisie, juste a
 * cote. `CreateMatch.jsx` libelle le champ `score_home` avec
 * `is_home ? notre club : adversaire` : le coach qui joue dehors y saisit les
 * buts adverses.
 *
 * L'autre point de saisie, le bandeau de `MatchDetail.jsx`, est oriente CLUB
 * (nous a gauche) et convertit par `toRawScores` avant d'ecrire. Il l'a
 * longtemps ecrit brut — c'est P0-SCORE-ECRITURE-001, decrit plus bas.
 *
 * POURQUOI CE MODULE EXISTE
 * -------------------------
 * Comparer `score_home` a `score_away` donne le resultat INVERSE sur tout
 * match joue a l'exterieur. Le 4 aout 2026 ce raccourci etait present dans
 * 12 endroits des deux depots : le match de Lucas contre Pau FC
 * (is_home=false, 2-0) affichait « Victoire » alors qu'il avait perdu 0-2, le
 * bilan de saison comptait 39 victoires au lieu de 35, et le meme 2-0 partait
 * aux familles par WhatsApp.
 *
 * Tout affichage de score, de resultat ou de bilan passe par ici, et toute
 * ECRITURE depuis un formulaire oriente club aussi (cf. `toRawScores`). Lire
 * `match.score_home` ailleurs est un defaut, sauf pour reproduire l'ordre du
 * terrain (cf. `homeAwayTeams`).
 *
 * Equivalent backend : `app/utils/match_result.py`. La convention est aussi
 * documentee a la definition des colonnes, dans `app/models/match.py`.
 */

/** Le club recoit-il ? `is_home` peut etre absent ou NULL — on retombe sur domicile. */
export function isHomeMatch(match) {
  return match?.is_home !== false
}

/**
 * [nous, eux] du point de vue du club, ou [null, null] si le score manque.
 * Un seul des deux scores saisi compte comme absent : afficher « 2 – ? »
 * laisserait croire a un resultat.
 */
export function scoresUsThem(match) {
  const sh = match?.score_home
  const sa = match?.score_away
  if (sh === null || sh === undefined || sa === null || sa === undefined) return [null, null]
  return isHomeMatch(match) ? [sh, sa] : [sa, sh]
}

/** Idem a la mi-temps. Meme convention sur les colonnes _ht. */
export function scoresUsThemHT(match) {
  const sh = match?.score_home_ht
  const sa = match?.score_away_ht
  if (sh === null || sh === undefined || sa === null || sa === undefined) return [null, null]
  return isHomeMatch(match) ? [sh, sa] : [sa, sh]
}

/**
 * Inverse de `scoresUsThem` — le couple (nous, eux) saisi par un coach vers
 * le couple `[score_home, score_away]` a ECRIRE, dans l'ordre du terrain.
 *
 * TOUT formulaire oriente club passe par ici. Ecrire `score_home = notre
 * score` sans regarder `is_home` est le defaut SYMETRIQUE de celui que ce
 * module corrige en lecture, et il est plus difficile a voir : la lecture
 * etant correcte, elle re-inverse fidelement une donnee fausse.
 *
 * C'est ce qui s'est produit le 15/08/2026 (P0-SCORE-ECRITURE-001). Le
 * bandeau de `MatchDetail` lisait oriente club et ecrivait brut. Un 1-4 saisi
 * a l'exterieur ressortait « Victoire 4-1 », et le coach a pu ressaisir
 * autant de fois qu'il voulait sans que rien ne bouge : l'aller-retour
 * ecriture -> lecture etait stable sur une valeur fausse.
 */
export function toRawScores(match, us, them) {
  return isHomeMatch(match) ? [us, them] : [them, us]
}

export function hasScore(match) {
  return scoresUsThem(match)[0] !== null
}

/** Buts marques par le club, ou null. */
export function goalsFor(match) {
  return scoresUsThem(match)[0]
}

/** Buts encaisses par le club, ou null. */
export function goalsAgainst(match) {
  return scoresUsThem(match)[1]
}

/** 'V' | 'N' | 'D' du point de vue du club, ou null sans score. */
export function resultLetter(match) {
  const [us, them] = scoresUsThem(match)
  if (us === null) return null
  if (us > them) return 'V'
  if (us === them) return 'N'
  return 'D'
}

/** 'Victoire' | 'Nul' | 'Défaite', ou null sans score. */
export function resultLabel(match) {
  const letter = resultLetter(match)
  return letter === null ? null : { V: 'Victoire', N: 'Nul', D: 'Défaite' }[letter]
}

export function isWin(match)    { return resultLetter(match) === 'V' }
export function isDraw(match)   { return resultLetter(match) === 'N' }
export function isDefeat(match) { return resultLetter(match) === 'D' }

/** Le club n'a rien encaisse. Faux sans score : un match non saisi n'est pas un clean sheet. */
export function isCleanSheet(match) {
  return goalsAgainst(match) === 0
}

/** '0-2', oriente club (le notre d'abord). null sans score. */
export function scoreDisplay(match, separator = '-') {
  const [us, them] = scoresUsThem(match)
  return us === null ? null : `${us}${separator}${them}`
}

/**
 * [equipe qui recoit, equipe qui se deplace] — l'ordre du TERRAIN.
 * A n'utiliser que pour reproduire l'affiche du match. Le score qui
 * l'accompagne reste alors `score_home` / `score_away` bruts, pour que les
 * deux orientations restent coherentes entre elles.
 */
export function homeAwayTeams(match, clubName, opponent) {
  const opp = opponent ?? match?.opponent ?? 'Adversaire'
  const club = clubName || 'Notre équipe'
  return isHomeMatch(match) ? [club, opp] : [opp, club]
}
